import { Stack } from '@mui/material';
import { EventTaskType, EVENT_TASK_TYPE_MAP } from '@sr/dto/dist/src/enums/events';
import { CompanyUrlParamsFilter } from 'entities/filters/company-url-params-filter';
import { DateUrlParamsFilter } from 'entities/filters/date-url-params-filter';
import { ManagerUrlParamsFilter } from 'entities/filters/manager-url-params-filter';
import { useManagersDropdownList } from 'features/user/user-picker/ui';
import { useSearchParams } from 'react-router-dom';
import { useCompanyLabel } from 'shared/hooks/use-entity-labels';
import { FilterWrapper, ValueFormatterContext, createRangeSummaryFormatter } from 'shared/ui/filter/filter-wrapper';
import { SimpleParamFilter } from 'shared/ui/param-filters/simple-param-filter';
import { EventTypeParamFilter } from './event-type-filter';

const EVENT_DIRECTIONS = [
  { id: 'out', label: 'Мы' },
  { id: 'in', label: 'Нам' },
];

const EVENT_BOOLEAN_FILTER = [
  { id: 'true', label: 'Да' },
];

export const EventsListFilter = ({
  disabledTaskType = true,
  isCompletedHidden = false,
  showCompanyFilter = false,
  showDirectionFilter = false,
}: {
  disabledTaskType?: boolean,
  isCompletedHidden?: boolean,
  showCompanyFilter?: boolean,
  showDirectionFilter?: boolean,
}) => {
  const { items: managers } = useManagersDropdownList('events');
  const [searchParams] = useSearchParams();
  const selectedCompanyId = searchParams.get('companyId');
  const companyLabel = useCompanyLabel(selectedCompanyId);

  const managerFormatter = ({ value }: ValueFormatterContext) => {
    const manager = managers.find((m) => String(m.id) === value);
    return manager ? `${manager.firstName} ${manager.secondName || ''}`.trim() : value;
  };

  const companyFormatter = ({ value }: ValueFormatterContext) => {
    if (companyLabel && selectedCompanyId === value) return companyLabel;
    return value;
  };

  const directionFormatter = ({ value }: ValueFormatterContext) => EVENT_DIRECTIONS.find((item) => item.id === value)?.label ?? value;
  const taskTypeFormatter = ({ value }: ValueFormatterContext) => EVENT_TASK_TYPE_MAP[value as EventTaskType] ?? value;
  const booleanFormatter = ({ value }: ValueFormatterContext) => (value === 'true' ? 'Да' : value);

  return (
    <FilterWrapper title="Фильтры дел" dense withCard={false}>
      <FilterWrapper.Item label="Менеджер" queryKeys="userId" valueFormatter={managerFormatter}>
        <ManagerUrlParamsFilter scope="events" queryParamName="userId" label="Менеджер" size="small" />
      </FilterWrapper.Item>

      {showCompanyFilter && (
        <FilterWrapper.Item label="Фирма" queryKeys="companyId" valueFormatter={companyFormatter}>
          <CompanyUrlParamsFilter queryParamName="companyId" label="Фирма" size="small" />
        </FilterWrapper.Item>
      )}

      {showDirectionFilter && (
        <FilterWrapper.Item label="Направление" queryKeys="direction" valueFormatter={directionFormatter}>
          <SimpleParamFilter queryParamName="direction" items={EVENT_DIRECTIONS} label="Направление" size="small" defaultValue="" />
        </FilterWrapper.Item>
      )}

      {disabledTaskType && (
        <FilterWrapper.Item label="Тип" queryKeys="taskType" valueFormatter={taskTypeFormatter}>
          <EventTypeParamFilter queryParamName="taskType" label="Тип" multi={true} size="small" />
        </FilterWrapper.Item>
      )}

      <FilterWrapper.Item
        label="Дата события"
        queryKeys={['fromDate', 'toDate']}
        summaryFormatter={createRangeSummaryFormatter({ fromKey: 'fromDate', toKey: 'toDate', emptyLabel: 'Дата события', activeLabel: 'Событие' })}
      >
        <Stack spacing={1.5}>
          <DateUrlParamsFilter queryParamName="fromDate" label="Дата события с" size="small" />
          <DateUrlParamsFilter queryParamName="toDate" label="Дата события по" size="small" />
        </Stack>
      </FilterWrapper.Item>

      {!showDirectionFilter && (
        <FilterWrapper.Item
          label="Назначено дело"
          queryKeys={['fromDueDate', 'toDueDate']}
          summaryFormatter={createRangeSummaryFormatter({ fromKey: 'fromDueDate', toKey: 'toDueDate', emptyLabel: 'Назначено дело', activeLabel: 'Назначено' })}
        >
          <Stack spacing={1.5}>
            <DateUrlParamsFilter queryParamName="fromDueDate" label="Назначено дело с" size="small" />
            <DateUrlParamsFilter queryParamName="toDueDate" label="Назначено дело по" size="small" />
          </Stack>
        </FilterWrapper.Item>
      )}

      <FilterWrapper.Item label="Только важные" queryKeys="isImportantOnly" valueFormatter={booleanFormatter}>
        <SimpleParamFilter queryParamName="isImportantOnly" items={EVENT_BOOLEAN_FILTER} label="Только важные" size="small" defaultValue="" />
      </FilterWrapper.Item>

      {isCompletedHidden && (
        <FilterWrapper.Item label="Скрыть выполненные" queryKeys="isCompletedHidden" valueFormatter={booleanFormatter}>
          <SimpleParamFilter queryParamName="isCompletedHidden" items={EVENT_BOOLEAN_FILTER} label="Скрыть выполненные" size="small" defaultValue="" />
        </FilterWrapper.Item>
      )}
    </FilterWrapper>
  );
};
